<template>
  <div class="resource-details-panel fixed inset-0 z-[108]">
    <div
      class="resource-details-panel__backdrop absolute inset-0 bg-black/25"
      @click="$emit('close')"
    />

    <aside
      ref="panel"
      class="resource-details-panel__drawer absolute top-0 right-0 h-full w-full max-w-sm bg-base-100 border-l border-base-300 shadow-xl flex flex-col"
      @mousedown.stop
    >
      <header class="resource-details-panel__header flex items-start justify-between gap-3 px-5 py-4 border-b border-base-300">
        <h2 class="text-base font-semibold leading-snug break-words pr-2">
          {{ details?.name || '—' }}
        </h2>
        <button
          type="button"
          class="btn btn-sm btn-ghost btn-square shrink-0"
          aria-label="Close"
          @click="$emit('close')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </header>

      <div class="resource-details-panel__body flex-1 overflow-y-auto px-5 py-4">
        <div
          v-if="loading"
          class="py-16 text-center"
        >
          <span class="loading loading-spinner loading-md" />
        </div>

        <template v-else-if="details">
          <section class="mb-6">
            <h3 class="text-xs font-semibold uppercase tracking-wide opacity-60 mb-3">
              Who has access
            </h3>

            <div class="flex items-center gap-2 mb-3">
              <div
                v-for="(user, index) in visibleUsers"
                :key="`${user.id}-${index}`"
                class="resource-details-panel__avatar"
                :title="user.name"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>

              <div
                v-if="details.access.extra_count > 0"
                class="resource-details-panel__avatar resource-details-panel__avatar--more"
                :title="`+${details.access.extra_count} more`"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                <span class="text-[10px] font-semibold">+{{ details.access.extra_count }}</span>
              </div>
            </div>

            <p class="text-sm opacity-80 leading-relaxed mb-3">
              {{ details.access.summary }}
            </p>

            <button
              v-if="details.can_admin"
              type="button"
              class="btn btn-sm btn-outline w-full"
              @click="$emit('manage-access')"
            >
              Manage access
            </button>
          </section>

          <section>
            <h3 class="text-xs font-semibold uppercase tracking-wide opacity-60 mb-3">
              {{ detailsSectionTitle }}
            </h3>

            <dl class="resource-details-panel__meta">
              <div class="resource-details-panel__meta-row">
                <dt>Type</dt>
                <dd>{{ resourceTypeLabel }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Location</dt>
                <dd>{{ details.location || notAvailable }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Owner</dt>
                <dd>{{ details.owned_by_you ? 'You' : (details.owner?.name || notAvailable) }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Created</dt>
                <dd>{{ details.created_at || notAvailable }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Last modified</dt>
                <dd>{{ details.updated_at || notAvailable }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Last opened</dt>
                <dd>{{ details.last_opened_at || notAvailable }}</dd>
              </div>
              <div class="resource-details-panel__meta-row">
                <dt>Description</dt>
                <dd>{{ details.description || notAvailable }}</dd>
              </div>
            </dl>
          </section>
        </template>

        <div
          v-else
          class="text-sm opacity-70 py-8 text-center"
        >
          Unable to load details.
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import { fetchResourceDetails } from '../lib/resource_details_api'
import { showToast } from '../lib/toast'

export default {
  name: 'ResourceDetailsPanel',
  props: {
    resource: {
      type: Object,
      required: true
    },
    refreshKey: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'manage-access'],
  data () {
    return {
      details: null,
      loading: true,
      notAvailable: 'Not available'
    }
  },
  computed: {
    detailsSectionTitle () {
      return this.resource.resourceType === 'TemplateFolder' ? 'Folder details' : 'Template details'
    },
    resourceTypeLabel () {
      return this.resource.resourceType === 'TemplateFolder' ? 'Folder' : 'Template'
    },
    visibleUsers () {
      return this.details?.access?.users || []
    }
  },
  watch: {
    refreshKey () {
      this.load()
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true

      try {
        const json = await fetchResourceDetails(this.resource)
        this.details = json.data
      } catch (error) {
        this.details = null
        showToast(error.message || 'Failed to load details', 'error')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
